/**
 * AdminBenchmarksPage — handoff §6.1.3.
 *
 * File picker + year input + Upload button. POST multipart to
 * /v1/admin/benchmarks/{year}; on success show row counts (parsed,
 * inserted, updated).
 *
 * Backend behavior is replace-all: existing rows for the year are
 * deleted, then the parsed CSV is inserted.
 */
import { useState } from "react";
import { useUploadBenchmarks } from "@/hooks/useUploadBenchmarks";
import type { BenchmarkUploadResponse } from "@/api/models";

export function AdminBenchmarksPage() {
  const upload = useUploadBenchmarks();
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<BenchmarkUploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    if (!file) return;
    setError(null);
    try {
      const data = await upload.mutateAsync({ year, file });
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
      setResult(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-tw-teal-400">
          Admin
        </div>
        <h1 className="text-2xl font-semibold text-gray-800">Benchmark upload</h1>
        <p className="mt-1 text-sm text-gray-600">
          Upload the CMS-published benchmark CSV for a performance year. The
          existing rows for that year are replaced.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-white p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label htmlFor="bench-year" className="block text-xs font-medium uppercase tracking-wider text-gray-500">
              Performance year
            </label>
            <input
              id="bench-year"
              type="number"
              min={2020}
              max={2099}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm tnum focus:border-tw-teal-500 focus:outline-none focus:ring-1 focus:ring-tw-teal-500"
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="bench-file" className="block text-xs font-medium uppercase tracking-wider text-gray-500">
              Benchmark CSV
            </label>
            <input
              id="bench-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:rounded file:border-0 file:bg-tw-teal-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-tw-teal-700 hover:file:bg-tw-teal-100"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={!file || upload.isPending}
            className="rounded-button bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-60"
          >
            {upload.isPending ? "Uploading…" : "Upload benchmarks"}
          </button>
          {file ? (
            <span className="text-xs text-gray-500">
              Ready to upload <span className="font-mono">{file.name}</span> (
              {(file.size / 1024).toFixed(1)} KB)
            </span>
          ) : null}
        </div>
      </div>

      {result ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
          <div className="text-sm font-semibold text-green-800">
            Uploaded {result.filename} for {result.performance_year}
          </div>
          <div className="mt-1 text-xs text-green-700 tnum">
            {result.rows_parsed} parsed · {result.rows_inserted} inserted ·{" "}
            {result.rows_updated} updated
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <div className="text-sm font-semibold text-red-800">Upload failed</div>
          <div className="mt-1 text-xs text-red-700">{error}</div>
        </div>
      ) : null}
    </div>
  );
}
